import { useMemo } from "react";

import type {
  CashierAdapter,
  CashierApplePayReadiness,
  CashierCryptoAsset,
  CashierFlow,
  CashierMethodView,
} from "../types";
import {
  paymentMethodRuntimeState,
  type PaymentMethodRuntimeState,
} from "./availability";
import { PaymentMethodBar } from "./PaymentMethodBar";

export type PaymentMethodPickerItem = {
  method: CashierMethodView;
  runtime: PaymentMethodRuntimeState;
};

export function PaymentMethodPicker({
  adapter,
  applePay,
  cryptoAssets,
  flow,
  methods,
  onSelect,
  selectedId,
}: {
  adapter: CashierAdapter | null;
  applePay: CashierApplePayReadiness;
  cryptoAssets: readonly CashierCryptoAsset[];
  flow: CashierFlow;
  methods: readonly CashierMethodView[];
  onSelect: (methodId: string) => void;
  selectedId: string | null;
}) {
  const items = useMemo<PaymentMethodPickerItem[]>(
    () =>
      methods.map((method) => ({
        method,
        runtime: paymentMethodRuntimeState({
          adapter,
          applePay,
          cryptoAssets,
          flow,
          method,
        }),
      })),
    [adapter, applePay, cryptoAssets, flow, methods],
  );

  if (!items.length) {
    return (
      <p className="cashier-payment-picker__empty" role="status">
        No payment methods are available.
      </p>
    );
  }

  const current =
    items.find(
      ({ method, runtime }) => method.id === selectedId && runtime.actionable,
    ) ??
    items.find(({ runtime }) => runtime.actionable) ??
    items[0] ??
    null;

  return (
    <PaymentMethodBar
      ariaLabel={
        flow === "deposit" ? "Deposit payment method" : "Withdrawal method"
      }
      current={
        current
          ? {
              id: current.method.id,
              label: current.method.label,
              detail: current.runtime.actionable
                ? current.method.detail
                : current.runtime.reason,
              disabled: !current.runtime.actionable,
            }
          : null
      }
      onSelect={onSelect}
      options={items.map(({ method, runtime }) => ({
        id: method.id,
        label: method.label,
        detail: runtime.actionable ? method.detail : runtime.reason,
        disabled: !runtime.actionable,
      }))}
    />
  );
}
